import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useCustomerSelection } from '../context/CustomerSelectionContext';
import { partnerService } from '../services/partnerService';
import { Customer, PartnerStaffUser } from '../types/partner';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Pencil, 
  X, 
  Check, 
  UserCircle,
  Briefcase,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface CustomerEditData {
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  contactNumber: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  industry: string;
}

export function CustomerView() {
  const [partnerStaff, setPartnerStaff] = useState<PartnerStaffUser[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<CustomerEditData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [assigningAccountManager, setAssigningAccountManager] = useState(false);
  const [selectedAccountManagerId, setSelectedAccountManagerId] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    contact: true,
    address: false,
    accountManager: true
  });
  
  const { partner, updateManagedCustomer } = useAuth();
  const { selectedCustomer: customer, isLoading: customerLoading, setSelectedCustomer } = useCustomerSelection();

  const syncCustomerState = (updatedCustomer: Customer) => {
    setSelectedCustomer(updatedCustomer);
    updateManagedCustomer(updatedCustomer);
  };

  const refreshCustomer = async (customerToRefresh: Customer) => {
    if (!partner?.partnerId) return;

    const response = await partnerService.getCustomerDetails(partner.partnerId, customerToRefresh.customerId);
    if (response.success && response.data) {
      syncCustomerState({ ...customerToRefresh, ...response.data });
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Load partner staff for account manager dropdown
  useEffect(() => {
    const loadPartnerStaff = async () => {
      if (!partner?.partnerId) return;

      try {
        const response = await partnerService.getPartnerStaff(partner.partnerId);
        if (response.success && response.data) {
          setPartnerStaff(response.data);
        }
      } catch (err) {
      }
    };

    loadPartnerStaff();
  }, [partner?.partnerId]);

  // Set initial account manager selection when customer changes
  useEffect(() => {
    if (customer) {
      const currentAM = partnerStaff.find(staff => 
        staff.firstName === customer.amFirstName &&
        staff.lastName === customer.amLastName &&
        staff.email === customer.amEmail
      );
      setSelectedAccountManagerId(currentAM?.partnerUserId || '');
    }
  }, [customer, partnerStaff]);

  // Start editing customer contact information
  const startEditing = (customer: Customer) => {
    setIsEditing(true);
    setEditData({
      adminFirstName: customer.adminFirstName,
      adminLastName: customer.adminLastName,
      adminEmail: customer.adminEmail,
      contactNumber: customer.contactNumber || '',
      country: customer.country || '',
      addressLine1: customer.addressLine1 || '',
      addressLine2: customer.addressLine2 || '',
      city: customer.city || '',
      state: customer.state || '',
      postalCode: customer.postalCode || '',
      industry: customer.industry || ''
    });
  };

  // Save customer contact information
  const saveCustomerContact = async () => {
    if (!partner?.partnerId || !customer || !editData) return;

    try {
      setError(null);
      const response = await partnerService.updateCustomerContact(partner.partnerId, customer.customerId, editData);
      
      if (response.success && response.data) {
        syncCustomerState({ ...customer, ...response.data });
        await refreshCustomer({ ...customer, ...response.data });
        setSuccessMessage('Customer contact information updated successfully');
        setIsEditing(false);
        setEditData(null);
      } else {
        throw new Error(response.error || 'Failed to update customer');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer');
    }
  };

  // Assign account manager to customer
  const assignAccountManager = async () => {
    if (!partner?.partnerId || !customer) return;

    try {
      setError(null);
      setAssigningAccountManager(true);
      
      const userIdToAssign = selectedAccountManagerId || null;
      
      const response = await partnerService.assignAccountManager(
        partner.partnerId,
        customer.customerId,
        userIdToAssign
      );
      
      if (response.success) {
        const updatedCustomer = { ...customer, ...(response.data || {}) };
        syncCustomerState(updatedCustomer);
        await refreshCustomer(updatedCustomer);
        setSuccessMessage(userIdToAssign ? 'Account manager assigned successfully' : 'Account manager unassigned successfully');
      } else {
        throw new Error(response.error || 'Failed to assign account manager');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign account manager');
    } finally {
      setAssigningAccountManager(false);
    }
  };

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (customerLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No customer selected</h3>
          <p className="text-sm text-slate-500 max-w-sm">
            Please select a customer from the sidebar to view and manage their information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Customer Details</h2>
              <p className="text-sm text-slate-500">{customer.customerName}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <X className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 flex items-center gap-2">
            <Check className="h-4 w-4 flex-shrink-0" />
            {successMessage}
          </div>
        )}

        {/* Contact Information Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-4 overflow-hidden">
          <button
            onClick={() => toggleSection('contact')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <UserCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-900">Contact Information</h3>
                <p className="text-sm text-slate-500">Admin contact and customer details</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing(customer);
                  }}
                  className="text-slate-600 hover:text-orange-600"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {expandedSections.contact ? (
                <ChevronUp className="h-5 w-5 text-slate-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-400" />
              )}
            </div>
          </button>

          {expandedSections.contact && (
            <div className="px-6 pb-6 border-t border-slate-100">
              {isEditing && editData ? (
                <div className="pt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="customer-admin-first-name" className="block text-sm font-medium text-slate-700 mb-1.5">First Name</label>
                      <input
                        id="customer-admin-first-name"
                        type="text"
                        value={editData.adminFirstName}
                        onChange={(e) => setEditData({ ...editData, adminFirstName: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="customer-admin-last-name" className="block text-sm font-medium text-slate-700 mb-1.5">Last Name</label>
                      <input
                        id="customer-admin-last-name"
                        type="text"
                        value={editData.adminLastName}
                        onChange={(e) => setEditData({ ...editData, adminLastName: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="customer-admin-email" className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                      <input
                        id="customer-admin-email"
                        type="email"
                        value={editData.adminEmail}
                        onChange={(e) => setEditData({ ...editData, adminEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="customer-contact-number" className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                      <input
                        id="customer-contact-number"
                        type="text"
                        value={editData.contactNumber}
                        onChange={(e) => setEditData({ ...editData, contactNumber: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="customer-industry" className="block text-sm font-medium text-slate-700 mb-1.5">Industry</label>
                      <input
                        id="customer-industry"
                        type="text"
                        value={editData.industry}
                        onChange={(e) => setEditData({ ...editData, industry: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={() => { setIsEditing(false); setEditData(null); }}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveCustomerContact}>
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <UserCircle className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">Admin Contact</div>
                      <div className="text-sm font-medium text-slate-900">
                        {customer.adminFirstName} {customer.adminLastName}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">Email</div>
                      <div className="text-sm font-medium text-slate-900">{customer.adminEmail}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">Phone</div>
                      <div className="text-sm font-medium text-slate-900">
                        {customer.contactNumber || <span className="text-slate-400">Not provided</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">Industry</div>
                      <div className="text-sm font-medium text-slate-900">
                        {customer.industry || <span className="text-slate-400">Not specified</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Address Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-4 overflow-hidden">
          <button
            onClick={() => toggleSection('address')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-900">Address</h3>
                <p className="text-sm text-slate-500">
                  {customer.city && customer.state 
                    ? `${customer.city}, ${customer.state}` 
                    : 'Business address details'}
                </p>
              </div>
            </div>
            {expandedSections.address ? (
              <ChevronUp className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            )}
          </button>

          {expandedSections.address && (
            <div className="px-6 pb-6 border-t border-slate-100">
              <div className="pt-4 space-y-3">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Street Address</div>
                  <div className="text-sm text-slate-900">
                    {customer.addressLine1 || <span className="text-slate-400">Not specified</span>}
                  </div>
                  {customer.addressLine2 && (
                    <div className="text-sm text-slate-900">{customer.addressLine2}</div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">City</div>
                    <div className="text-sm text-slate-900">
                      {customer.city || <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">State</div>
                    <div className="text-sm text-slate-900">
                      {customer.state || <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Postal Code</div>
                    <div className="text-sm text-slate-900">
                      {customer.postalCode || <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Country</div>
                    <div className="text-sm text-slate-900">
                      {customer.country || <span className="text-slate-400">—</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Account Manager Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <button
            onClick={() => toggleSection('accountManager')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-slate-900">Account Manager</h3>
                <p className="text-sm text-slate-500">
                  {customer.amFirstName && customer.amLastName 
                    ? `${customer.amFirstName} ${customer.amLastName}` 
                    : 'Assign an account manager'}
                </p>
              </div>
            </div>
            {expandedSections.accountManager ? (
              <ChevronUp className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            )}
          </button>

          {expandedSections.accountManager && (
            <div className="px-6 pb-6 border-t border-slate-100">
              <div className="pt-4 space-y-4">
                {/* Current Assignment */}
                {(customer.amFirstName || customer.amLastName || customer.amEmail) && (
                  <div className="p-4 bg-purple-50 rounded-xl">
                    <div className="text-xs font-medium text-purple-600 mb-2">Current Assignment</div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold">
                        {customer.amFirstName?.charAt(0)}{customer.amLastName?.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {customer.amFirstName} {customer.amLastName}
                        </div>
                        {customer.amEmail && (
                          <div className="text-sm text-slate-600">{customer.amEmail}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Change Account Manager */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {customer.amFirstName ? 'Change Account Manager' : 'Assign Account Manager'}
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={selectedAccountManagerId}
                      onChange={(e) => setSelectedAccountManagerId(e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white"
                      disabled={assigningAccountManager}
                      aria-label="Select account manager"
                    >
                      <option value="">— No Account Manager —</option>
                      {partnerStaff.map((staff) => (
                        <option key={staff.partnerUserId} value={staff.partnerUserId}>
                          {staff.firstName} {staff.lastName} ({staff.email})
                        </option>
                      ))}
                    </select>
                    <Button 
                      size="sm" 
                      onClick={assignAccountManager}
                      disabled={assigningAccountManager}
                    >
                      {assigningAccountManager ? 'Saving...' : 'Update'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

// For backwards compatibility, also export as TenantsView
export const TenantsView = CustomerView;